import {
  Box,
  Typography,
  Grid,
  TextField,
  FormLabel,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ProductRow from "./product-row";
import CustomTypography from "../../globalStyles/fonts";
import AddressLocationFields from "../AddressLocation";
import { useDarkMode } from "../context/darkModeContext";

const requiredFields = [
  { name: "first_name", label: "First Name" },
  { name: "last_name", label: "Last Name" },
  { name: "address1", label: "Address 1" },
  { name: "address2", label: "Address 2", optional: true },
  { name: "company", label: "Company", optional: true },
  { name: "email", label: "Email", type: "email" },
];

const CreateOrder = ({
  m2oData,
  onM2ODataChange,
  mockProducts,
  errors,
  setErrors,
  handleAttachmentChange,
  clientId,
  sameAsShippingAddress,
  setSameAsShippingAddress,
  onShippingAddressChange = () => {},
  onCustomerAddressChange = () => {},
}) => {
  const { colors } = useDarkMode();

  const handleBasicInfoChange = (field, value, section = "main") => {
    // Clear relevant error when user starts typing
    if (setErrors && errors) {
      const newErrors = { ...errors };

      // Clear field-specific errors
      if (section === "shipping_address") {
        delete newErrors[`shipping_address_${field}`];
        delete newErrors[field]; // Also clear the generic field error
      } else if (section === "customer") {
        delete newErrors[`customer_${field}`];
        delete newErrors[field]; // Also clear the generic field error
      } else {
        delete newErrors[field];
      }

      setErrors(newErrors);
    }

    if (section === "shipping_address") {
      onShippingAddressChange({
        [field]: value,
      });
    } else if (section === "customer") {
      onCustomerAddressChange({
        [field]: value,
      });
    } else {
      onM2ODataChange({
        [field]: value,
      });
    }
  };

  const handleProvinceSelect = (type, value) => {
    // Clear related address errors
    if (setErrors && errors) {
      const newErrors = { ...errors };
      delete newErrors[`${type}_province`];
      delete newErrors[`${type}_province_code`];
      delete newErrors[`${type}_country`];
      delete newErrors[`${type}_country_code`];
      delete newErrors["province"];
      delete newErrors["province_code"];
      delete newErrors["country"];
      delete newErrors["country_code"];
      setErrors(newErrors);
    }

    if (type === "shipping_address") {
      onShippingAddressChange({
        province: value?.name || "",
        province_code: value?.code || "",
        // country: value?.country?.name || "",
        // country_code: value?.country?.code2 || "",
      });
      return;
    }
    if (type === "customer") {
      onCustomerAddressChange({
        province: value?.name || "",
        province_code: value?.code || "",
        // country: value?.country?.name || "",
        // country_code: value?.country?.code2 || "",
      });
      return;
    }
  };

  const handleProvinceChange = (type, value) => {
    // Clear related address errors
    if (setErrors && errors) {
      const newErrors = { ...errors };
      delete newErrors[`${type}_province`];
      delete newErrors[`${type}_province_code`];
      delete newErrors["province"];
      delete newErrors["province_code"];
      setErrors(newErrors);
    }

    if (type === "shipping_address") {
      onShippingAddressChange({
        province: value?.name || "",
        province_code: value?.code || "",
      });
      return;
    }
    if (type === "customer") {
      onCustomerAddressChange({
        province: value?.name || "",
        province_code: value?.code || "",
      });
      return;
    }
  };

  const handleCountryChange = (type, value) => {
    const isCountryCleared = !value?.name;

    if (setErrors && errors) {
      const newErrors = { ...errors };
      delete newErrors[`${type}_country`];
      delete newErrors[`${type}_country_code`];
      delete newErrors[`${type}_province`];
      delete newErrors[`${type}_province_code`];
      delete newErrors[`${type}_city`];
      delete newErrors["country"];
      delete newErrors["country_code"];
      delete newErrors["province"];
      delete newErrors["province_code"];
      delete newErrors["city"];
      setErrors(newErrors);
    }

    const addressUpdate = {
      country: value?.name || "",
      country_code: value?.code2 || "",
    };

    if (isCountryCleared) {
      addressUpdate.province = "";
      addressUpdate.province_code = "";
      addressUpdate.city = "";
    }

    if (type === "shipping_address") {
      onShippingAddressChange(addressUpdate);
      return;
    }

    if (type === "customer") {
      onCustomerAddressChange(addressUpdate);
      return;
    }
  };

  const handleProductUpdate = (index, updatedProduct) => {
    // Clear product-specific errors when user updates a product
    if (setErrors && errors && errors.sku_list) {
      const newErrors = { ...errors };
      if (newErrors.sku_list && newErrors.sku_list[index]) {
        newErrors.sku_list[index] = {};
      }
      setErrors(newErrors);
    }

    const updatedProducts = [...m2oData.sku_list];
    updatedProducts[index] = updatedProduct;
    onM2ODataChange({
      ...m2oData,
      sku_list: updatedProducts,
    });
  };

  const handleProductRemove = (index) => {
    const updatedProducts = m2oData.sku_list.filter((_, i) => i !== index);
    onM2ODataChange({
      ...m2oData,
      sku_list: updatedProducts,
    });
  };

  const addProduct = () => {
    onM2ODataChange({
      ...m2oData,
      sku_list: [
        ...m2oData.sku_list,
        {
          product: "",
          sku: "",
          variant_id: "",
          quantity_requested: "",
          quantity_accepted: "",
          price: "",
          instructions: "",
          attachment: null,
          attachmentUrl: null,
          no_of_overpacks: "",
          overpack_type: "",
          items_per_overpack: "",
        },
      ],
    });
  };

  const handleSameAddressChange = (event) => {
    const isChecked = event.target.checked;
    setSameAsShippingAddress(isChecked);

    // Clear customer address errors when "same as shipping" is checked
    if (isChecked && setErrors && errors) {
      const newErrors = { ...errors };
      // Clear all customer-related errors
      const customerFields = [
        "first_name",
        "last_name",
        "address1",
        "city",
        "zip",
        "province",
        "province_code",
        "country",
        "country_code",
        "email",
      ];
      customerFields.forEach((field) => {
        delete newErrors[`customer_${field}`];
        delete newErrors[field];
      });
      setErrors(newErrors);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Typography
        fontWeight={600}
        fontFamily="Poppins"
        textAlign="left"
        variant="h6"
        gutterBottom
      >
        1. Basic Information
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={12} sx={{ textAlign: "left" }}>
          <FormLabel
            sx={{
              fontSize: "0.875rem",
              color: colors.textPrimary,
              fontFamily: "Poppins",
            }}
            required
          >
            Order ID
          </FormLabel>
          <TextField
            style={{ marginTop: "8px" }}
            fullWidth
            size="small"
            value={m2oData.order_id || ""}
            onChange={(e) => handleBasicInfoChange("order_id", e.target.value)}
            placeholder="Enter Order ID"
            error={!!errors.order_id}
            helperText={errors.order_id}
          />
        </Grid>
      </Grid>

      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
            mb: 1,
          }}
        >
          <Typography fontFamily="Poppins" fontWeight={600} variant="h6">
            2. Products
          </Typography>
        </Box>

        {m2oData.sku_list?.map((product, index) => (
          <ProductRow
            m2oData={m2oData}
            key={index}
            index={index}
            product={product}
            mockProducts={mockProducts}
            clientId={clientId ? { _id: clientId } : m2oData.client_id}
            onUpdate={handleProductUpdate}
            onRemove={handleProductRemove}
            errors={errors?.sku_list?.[index] || {}}
            setErrors={setErrors}
            allErrors={errors}
            handleAttachmentChange={handleAttachmentChange}
          />
        ))}

        <CustomTypography
          onClick={addProduct}
          style={{ color: "#0040FF", textAlign: "right", cursor: "pointer" }}
          size="small"
        >
          Add Product
        </CustomTypography>
      </Box>

      <Box>
        <Typography
          fontWeight={600}
          fontFamily="Poppins"
          textAlign="left"
          variant="h6"
        >
          3. Shipping Address
        </Typography>

        <Grid container spacing={2} mt={1}>
          {requiredFields.map(({ name, label, optional, type }) => (
            <Grid
              key={name}
              item
              xs={12}
              md={4}
              style={{ textAlign: "left", paddingTop: "3px" }}
            >
              <FormLabel
                sx={{
                  fontSize: "0.875rem",
                  fontFamily: "Poppins",
                  color: colors.textPrimary,
                }}
                required={!optional}
              >
                {label}
              </FormLabel>

              <TextField
                style={{ marginTop: "8px" }}
                fullWidth
                size="small"
                type={type || "text"}
                value={m2oData.shipping_address?.[name] || ""}
                onChange={(e) =>
                  handleBasicInfoChange(
                    name,
                    e.target.value,
                    "shipping_address",
                  )
                }
                error={!!errors[name]}
                helperText={errors[name]}
              />
            </Grid>
          ))}

          <AddressLocationFields
            type="shipping_address"
            address={m2oData.shipping_address}
            onCountryChange={(country) =>
              handleCountryChange("shipping_address", country)
            }
            onProvinceChange={(province) =>
              handleProvinceChange("shipping_address", province)
            }
            onProvinceSelect={(province_code) =>
              handleProvinceSelect("shipping_address", province_code)
            }
            onZipChange={(zip) =>
              handleBasicInfoChange("zip", zip, "shipping_address")
            }
            errors={errors}
            onCityChange={(city) =>
              handleBasicInfoChange("city", city, "shipping_address")
            }
          />
        </Grid>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={sameAsShippingAddress}
            onChange={handleSameAddressChange}
            sx={{
              color: "#0040FF",
              "&.Mui-checked": {
                color: "#0040FF",
              },
            }}
          />
        }
        label={
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontSize: "0.875rem",
              color: colors.textPrimary,
            }}
          >
            Mark Shipping address same as Customer's Address
          </Typography>
        }
        sx={{ mb: 1, mt: 1 }}
      />

      {!sameAsShippingAddress && (
        <Box>
          <Typography
            fontWeight={600}
            fontFamily="Poppins"
            textAlign="left"
            variant="h6"
            gutterBottom
          >
            4. Customer Information
          </Typography>

          <Grid container spacing={2} mt={1}>
            {requiredFields.map(({ name, label, optional, type }) => (
              <Grid key={name} item xs={12} md={4} sx={{ textAlign: "left" }}>
                <FormLabel
                  sx={{
                    mb: 1,
                    fontSize: "0.875rem",
                    fontFamily: "Poppins",
                    color: colors.textPrimary,
                  }}
                  required={!optional}
                >
                  {label}
                </FormLabel>

                <TextField
                  style={{ marginTop: "8px" }}
                  fullWidth
                  size="small"
                  type={type || "text"}
                  value={m2oData.customer?.[name] || ""}
                  onChange={(e) =>
                    handleBasicInfoChange(name, e.target.value, "customer")
                  }
                  error={!!errors[name]}
                  helperText={errors[name]}
                />
              </Grid>
            ))}
            <AddressLocationFields
              type="customer"
              address={m2oData.customer}
              onCountryChange={(country) =>
                handleCountryChange("customer", country)
              }
              onProvinceChange={(province) =>
                handleProvinceChange("customer", province)
              }
              onProvinceSelect={(province_code) =>
                handleProvinceSelect("customer", province_code)
              }
              onCityChange={(city) =>
                handleBasicInfoChange("city", city, "customer")
              }
              onZipChange={(zip) =>
                handleBasicInfoChange("zip", zip, "customer")
              }
              errors={errors}
            />
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default CreateOrder;
